import type { GameState, TaskItem, FortuneDisplay } from '@/types'
import { apiService } from '@/services/api'
import { createIcons, Star, Check, Share2 } from 'lucide'

type ShareableFortune = {
  message: string
  tier?: string | null
  stars?: number | null
}

export class UIManager {
  private cracksHeadline: HTMLElement | null
  private cracksLeftSpan: HTMLElement
  private totalCrackedSpan: HTMLElement
  private cracksTimer: HTMLElement
  private timerValue: HTMLElement | null
  private fortuneBox: HTMLElement
  private fortuneActions: HTMLElement
  private shareBtn: HTMLButtonElement
  private resetBtn: HTMLElement | null
  private purchaseSection: HTMLElement
  private buyMoreBtn: HTMLElement
  private modalBackdrop: HTMLElement
  private quantityInput: HTMLInputElement
  private decreaseBtn: HTMLButtonElement
  private increaseBtn: HTMLButtonElement
  private totalCracksSpan: HTMLElement
  private totalStarsSpan: HTMLElement
  private customPacksSpan: HTMLElement
  private buyCustomBtn: HTMLElement
  private closeQuantityBtn: HTMLElement
  private buyBtn: HTMLElement
  private crackAllBtn: HTMLElement
  private refEarnedSpan: HTMLElement
  private refillTimer: number | null = null
  private tasksList: HTMLOListElement | null
  private currentFortune: string = ''
  private currentFortuneDetails: FortuneDisplay | null = null
  private fortuneImagePromise: Promise<HTMLImageElement> | null = null
  private shareButtonDefaultHtml: string
  private shareButtonResetTimer: number | null = null
  private referralUrl: string | null = null
  private shareModalBackdrop: HTMLElement
  private closeShareModalBtn: HTMLElement
  private shareToStoryBtn: HTMLElement
  private shareToChatBtn: HTMLElement
  private watchRewardedAdBtn: HTMLElement
  private readonly shareImageWidth = 1080
  private readonly shareImageHeight = 1920

  constructor() {
    // Try to get both old and new elements for compatibility
    this.cracksHeadline = document.getElementById('cracksHeadline')
    this.cracksLeftSpan = document.getElementById('cracksLeft')!
    this.totalCrackedSpan = document.querySelector('.total-inline')!
    this.cracksTimer = document.getElementById('cracksTimer')!
    this.timerValue = document.querySelector('.timer-value')
    this.fortuneBox = document.getElementById('fortune')!
    this.fortuneActions = document.getElementById('fortuneActions')!
    this.shareBtn = document.getElementById('shareFortune')! as HTMLButtonElement
    this.resetBtn = document.getElementById('reset')
    this.purchaseSection = document.getElementById('purchaseSection')!
    this.buyMoreBtn = document.getElementById('buyMoreBtn')!
    this.modalBackdrop = document.getElementById('modalBackdrop')!
    this.quantityInput = document.getElementById('packsQuantity')! as HTMLInputElement
    this.decreaseBtn = document.getElementById('decreaseQuantity')! as HTMLButtonElement
    this.increaseBtn = document.getElementById('increaseQuantity')! as HTMLButtonElement
    this.totalCracksSpan = document.getElementById('totalCracks')!
    this.totalStarsSpan = document.getElementById('totalStars')!
    this.customPacksSpan = document.getElementById('customPacks')!
    this.buyCustomBtn = document.getElementById('buyCustom')!
    this.closeQuantityBtn = document.getElementById('closeQuantitySelector')!
    this.buyBtn = document.getElementById('buyCracks')!
    this.crackAllBtn = document.getElementById('crackAll')!
    this.refEarnedSpan = document.getElementById('refEarned')!
    this.tasksList = document.getElementById('tasksList') as HTMLOListElement | null
    this.shareModalBackdrop = document.getElementById('shareModalBackdrop')!
    this.closeShareModalBtn = document.getElementById('closeShareModal')!
    this.shareToStoryBtn = document.getElementById('shareToStory')!
    this.shareToChatBtn = document.getElementById('shareToChat')!
    this.watchRewardedAdBtn = document.getElementById('watchRewardedAd')!
    this.shareButtonDefaultHtml = this.shareBtn.innerHTML

    this.setupShareButton()
    this.setupPurchaseInterface()
  }

  updateFromGameState(state: GameState, isCracked: boolean): void {
    this.renderCracksHeadline(state)
    this.updateCrackUI(state, isCracked)
    this.referralUrl = state.refCode
      ? `https://t.me/a_fortune_cookie_bot/cookie?startapp=${state.refCode}`
      : null
    
    // Dispatch event to notify that game state has been updated
    window.dispatchEvent(new CustomEvent('gameStateUpdated', { detail: state }))
  }

  renderTasks(tasks: TaskItem[]): void {
    if (!this.tasksList) return
    if (!tasks || tasks.length === 0) {
      this.tasksList.innerHTML = '<li class="task-row">Nothing to do (yet)…</li>'
      return
    }

    // Group tasks by category
    const taskGroups = this.groupTasks(tasks)
    
    let html = ''
    for (const [groupName, groupTasks] of Object.entries(taskGroups)) {
      if (groupTasks.length === 0) continue
      
      html += `<li class="task-group-header">${groupName}</li>`
      
      const groupHtml = groupTasks
        .map((t) => {
          const pct = t.need ? Math.min(100, Math.round((t.progress / t.need) * 100)) : 0
          const showBar = t.kind !== 'tg_member' && t.kind !== 'bot_started' && t.need !== null

          const progress = showBar
            ? `
            <div class="t-progress"><div style="width:${pct}%"></div></div>
            <div class="t-sub">${t.progress}/${t.need} · Reward +${t.reward} Total Cracks</div>
          `
            : `<div class="t-sub">Reward +${t.reward} Total Cracks</div>`

          let cta = ''
          if (t.claimed) {
            cta = `<span class="t-badge">Claimed</span>`
          } else if (t.kind === 'tg_member') {
            // show both: Join (opens Telegram) and Verify (checks membership)
            cta = `
              <div class="t-cta-group">
                <button class="inventory-marketplace-btn secondary" data-action="join" data-join-url="${t.join_url}">
                  Join
                </button>
                <button class="inventory-marketplace-btn" data-action="verify" data-task-id="${t.id}">
                  Verify
                </button>
              </div>
            `
          } else if (t.kind === 'bot_started') {
            // show both: Start (opens Telegram bot) and Verify (checks if bot was started)
            cta = `
              <div class="t-cta-group">
                <button class="inventory-marketplace-btn secondary" data-action="start" data-start-url="${t.start_url}">
                  Start
                </button>
                <button class="inventory-marketplace-btn" data-action="verify" data-task-id="${t.id}">
                  Verify
                </button>
              </div>
            `
          } else if (t.kind === 'apps_center_vote') {
            // show both: Vote (opens voting page) and Verify (confirms voting)
            cta = `
              <div class="t-cta-group">
                <button class="inventory-marketplace-btn secondary" data-action="vote" data-vote-url="${t.vote_url}">
                  Vote
                </button>
                <button class="inventory-marketplace-btn" data-action="verify" data-task-id="${t.id}">
                  Verify
                </button>
              </div>
            `
          } else if (t.ready) {
            cta = `<button class="inventory-marketplace-btn" data-action="claim" data-task-id="${t.id}">Claim</button>`
          } else {
            cta = `<button class="inventory-marketplace-btn" disabled>In progress</button>`
          }

          return `
          <li class="task-row">
            <div class="t-main">
              <div class="t-title">${t.title}</div>
              ${progress}
            </div>
            <div class="t-cta">${cta}</div>
          </li>
        `
        })
        .join('')
      
      html += groupHtml
    }

    this.tasksList.innerHTML = html
  }

  private groupTasks(tasks: TaskItem[]): Record<string, TaskItem[]> {
    const groups: Record<string, TaskItem[]> = {
      'Getting Started': [],
      'Cracking Milestones': [],
      'Streak Challenges': [],
      'Marketplace Activities': [],
    }

    tasks.forEach(task => {
      switch (task.kind) {
        case 'tg_member':
        case 'bot_started':
        case 'apps_center_vote':
          groups['Getting Started'].push(task)
          break
        case 'counter':
          groups['Cracking Milestones'].push(task)
          break
        case 'streak':
          groups['Streak Challenges'].push(task)
          break
        case 'marketplace_buy':
        case 'marketplace_sell':
          groups['Marketplace Activities'].push(task)
          break
        default:
          // Fallback to Getting Started for unknown types
          groups['Getting Started'].push(task)
      }
    })

    return groups
  }

  private renderCracksHeadline(state: GameState): void {
    // Update new layout elements - the HTML already has the /10 part
    this.cracksLeftSpan.textContent = state.cracksLeft.toString()
    this.totalCrackedSpan.textContent = state.totalCracked.toString()
    
    // Update old layout element if it exists (for compatibility)
    if (this.cracksHeadline) {
      this.cracksHeadline.innerHTML = `
        ${state.cracksLeft}<span class="max">/10</span> energy left
        <span class="sep">·</span>
        <span class="total-inline">${state.totalCracked} Total Cracks</span>
      `
    }
  }

  private updateCrackUI(state: GameState, isCracked: boolean): void {
    const empty = state.cracksLeft === 0

    if (!empty) {
      document.body.classList.remove('zero-left')
      this.cracksTimer.hidden = true
      this.fortuneBox.hidden = false
      this.clearRefillTimer()
    } else {
      document.body.classList.add('zero-left')
      this.cracksTimer.hidden = false
      this.fortuneBox.hidden = !isCracked
      if (!this.refillTimer) {
        this.startRefillCountdown()
      }
    }

    this.refEarnedSpan.textContent = state.refEarned.toString()
    this.purchaseSection.classList.toggle('show', empty)

    // Show/hide rewarded ad button based on energy and daily limit
    const showRewardedAd = empty && state.canWatchRewardedAd
    
    console.log('[UI] Updating rewarded ad button visibility:', {
      empty,
      canWatchRewardedAd: state.canWatchRewardedAd,
      showRewardedAd,
      cracksLeft: state.cracksLeft
    })
    
    this.watchRewardedAdBtn.style.display = showRewardedAd ? 'inline-flex' : 'none'

    const buyMoreBtn = document.getElementById('buyMoreCracks')
    if (buyMoreBtn) {
      buyMoreBtn.classList.toggle('show', empty)
    }
    this.buyMoreBtn.classList.toggle('show', empty)

    // Also show the individual buy button when cracks are empty
    const buyBtn = document.getElementById('buyCracks')
    if (buyBtn) {
      buyBtn.classList.toggle('show', empty)
    }
    this.buyBtn.classList.toggle('show', empty)
    
    const crackAllBtn = document.getElementById('crackAll')
    if (crackAllBtn) {
      crackAllBtn.classList.toggle('show', !empty && !isCracked)
    }
    this.crackAllBtn.classList.toggle('show', !empty && !isCracked)

    // Reset button visibility is handled by AnimationService in App.ts
    // Only hide it when we need to reset (not cracked or no cracks left)
    if ((!isCracked || empty) && this.resetBtn) {
      this.resetBtn.style.display = 'none'
      this.resetBtn.style.opacity = '0'
    }
  }

  private startRefillCountdown(): void {
    this.clearRefillTimer()

    const now = new Date()
    const next = new Date(now)
    next.setUTCHours(24, 0, 0, 0)
    let diff = Math.floor((next.getTime() - now.getTime()) / 1000)

    const tick = (): void => {
      const hours = String(Math.floor(diff / 3600)).padStart(2, '0')
      const minutes = String(Math.floor(diff / 60) % 60).padStart(2, '0')
      const seconds = String(diff % 60).padStart(2, '0')
      
      // Update timer display - support both old and new formats
      if (this.timerValue) {
        this.timerValue.textContent = `${hours}:${minutes}:${seconds}`
      } else {
        this.cracksTimer.textContent = `Energy refill in ${hours}:${minutes}:${seconds}`
      }

      if (diff-- <= 0) {
        this.clearRefillTimer()
        // Trigger refresh - this should be handled by the main app
        window.dispatchEvent(new CustomEvent('refreshState'))
      }
    }

    tick()
    this.refillTimer = window.setInterval(tick, 1000)
  }

  private clearRefillTimer(): void {
    if (this.refillTimer) {
      clearInterval(this.refillTimer)
      this.refillTimer = null
    }
  }

  showFortune(fortune: FortuneDisplay | string): void {
    if (typeof fortune === 'string') {
      // Handle legacy string format for milestone fortunes
      this.currentFortune = fortune
      this.currentFortuneDetails = null
      this.fortuneBox.innerHTML = this.buildFortuneCard({ message: fortune, quote: false })
    } else {
      // Handle new FortuneDisplay format
      this.currentFortune = fortune.message
      this.currentFortuneDetails = fortune

      this.fortuneBox.innerHTML = this.buildFortuneCard({
        message: fortune.message,
        tier: fortune.tier,
        stars: fortune.stars,
      })

      // Initialize Lucide icons for the newly added star icons
      createIcons({
        icons: { Star },
      })
    }
    // Animation will be handled by AnimationService
  }

  clearFortune(): void {
    this.currentFortune = ''
    this.currentFortuneDetails = null
    this.fortuneBox.textContent = ''
    this.fortuneActions.style.display = 'none'
    // Also hide reset button when clearing fortune
    if (this.resetBtn) {
      this.resetBtn.style.display = 'none'
      this.resetBtn.style.opacity = '0'
    }
  }

  private setupShareButton(): void {
    // Main share button opens the modal
    this.shareBtn.addEventListener('click', () => {
      this.showShareModal()
    })

    // Close modal handlers
    this.closeShareModalBtn.addEventListener('click', () => {
      this.hideShareModal()
    })

    this.shareModalBackdrop.addEventListener('click', (e) => {
      if (e.target === this.shareModalBackdrop) {
        this.hideShareModal()
      }
    })

    // Share option handlers
    this.shareToStoryBtn.addEventListener('click', () => {
      this.hideShareModal()
      import('@/services/analytics').then(({ track }) => {
        track('share_fortune', { channel: 'telegram-story' })
      })
      this.shareToTelegramStory()
    })

    this.shareToChatBtn.addEventListener('click', () => {
      this.hideShareModal()
      import('@/services/analytics').then(({ track }) => {
        track('share_fortune', { channel: 'telegram-chat' })
      })
      this.shareToTelegramChat()
    })

    


  }

  private showShareModal(): void {
    this.shareModalBackdrop.style.display = 'flex'
    
    // Re-initialize icons after modal is shown
    import('lucide').then(({ createIcons, Camera, MessageCircle }) => {
      createIcons({
        icons: { Camera, MessageCircle }
      })
    })
    document.body.style.overflow = 'hidden'
  }

  private hideShareModal(): void {
    this.shareModalBackdrop.style.display = 'none'
    document.body.style.overflow = ''
  }

  private setupPurchaseInterface(): void {
    // Show/hide quantity selector
    this.buyMoreBtn.addEventListener('click', () => {
      this.modalBackdrop.style.display = 'flex'
      document.body.style.overflow = 'hidden' // Prevent background scrolling
    })

    this.closeQuantityBtn.addEventListener('click', () => {
      this.closeQuantitySelector()
    })

    // Handle backdrop click to close
    this.modalBackdrop.addEventListener('click', (e) => {
      if (e.target === this.modalBackdrop) {
        this.closeQuantitySelector()
      }
    })

    // Handle preset buttons
    const presetBtns = document.querySelectorAll('.preset-btn')
    presetBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const packs = parseInt(btn.getAttribute('data-packs') || '1')
        this.selectQuantity(packs)
        // Close selector and trigger purchase
        this.closeQuantitySelector()
        // Dispatch custom event for purchase with specific quantity
        window.dispatchEvent(new CustomEvent('purchaseWithQuantity', { detail: { packs } }))
      })
    })

    // Handle custom quantity controls
    this.quantityInput.addEventListener('input', () => {
      this.updateCustomQuantityDisplay()
    })

    this.decreaseBtn.addEventListener('click', () => {
      const current = parseInt(this.quantityInput.value) || 1
      if (current > 1) {
        this.quantityInput.value = (current - 1).toString()
        this.updateCustomQuantityDisplay()
      }
    })

    this.increaseBtn.addEventListener('click', () => {
      const current = parseInt(this.quantityInput.value) || 1
      if (current < 99) {
        this.quantityInput.value = (current + 1).toString()
        this.updateCustomQuantityDisplay()
      }
    })

    this.quantityInput.addEventListener('blur', () => {
      let value = parseInt(this.quantityInput.value) || 1
      value = Math.max(1, Math.min(99, value))
      this.quantityInput.value = value.toString()
      this.updateCustomQuantityDisplay()
    })

    // Handle custom buy button
    this.buyCustomBtn.addEventListener('click', () => {
      const packs = parseInt(this.quantityInput.value) || 1
      this.closeQuantitySelector()
      window.dispatchEvent(new CustomEvent('purchaseWithQuantity', { detail: { packs } }))
    })

    // Initial display update
    this.updateCustomQuantityDisplay()
  }

  private selectQuantity(packs: number): void {
    this.quantityInput.value = packs.toString()
    this.updateCustomQuantityDisplay()
  }

  private closeQuantitySelector(): void {
    this.modalBackdrop.style.display = 'none'
    document.body.style.overflow = '' // Restore scrolling
  }

  private updateCustomQuantityDisplay(): void {
    const quantity = parseInt(this.quantityInput.value) || 1
    const totalEnergy = quantity * 5
    const totalStars = quantity * 10

    this.totalCracksSpan.textContent = totalEnergy.toString()
    this.totalStarsSpan.textContent = totalStars.toString()
    this.customPacksSpan.textContent = quantity.toString()

    // Update button states
    this.decreaseBtn.disabled = quantity <= 1
    this.increaseBtn.disabled = quantity >= 99
  }

  getSelectedQuantity(): number {
    return parseInt(this.quantityInput.value) || 1
  }

  // For backward compatibility - default purchase is always 1 pack
  getDefaultQuantity(): number {
    return 1
  }

  // Converts Blob → base64 data URL
private blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('Failed to convert blob to base64'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to convert blob to base64'))
    reader.readAsDataURL(blob)
  })
}
private errorToString(err: unknown): string {
  try {
    if (err instanceof Error) return `${err.name}: ${err.message}`;
    if (typeof Event !== 'undefined' && err instanceof Event) {
      const ev = err as Event;
      const t = ev.target as any;
      if (t && t.tagName === 'IMG') {
        return `Event(${ev.type}) <img src="${t.currentSrc || t.src || ''}">`;
      }
      return `Event(${ev.type})`;
    }
    if (typeof err === 'object' && err) return JSON.stringify(err as any);
    return String(err);
  } catch {
    return String(err);
  }
}
// Uploads the image to imgbb and returns a public HTTPS image URL
private async uploadToImgBB(blob: Blob): Promise<string> {
  const dataUrl = await this.blobToBase64(blob)
  const base64 = dataUrl.split(',')[1] ?? dataUrl // strip "data:image/jpeg;base64,"

  const fd = new FormData()
  fd.append('image', base64)
  fd.append('name', `fortune_${Date.now()}`)

  const resp = await fetch('https://api.imgbb.com/1/upload?key=14c8286de4575d5a3afd04ffad5f719b', {
    method: 'POST',
    body: fd,
  })
  if (!resp.ok) throw new Error(`imgbb upload failed: ${resp.status}`)

  const json = await resp.json()
  // Prefer the direct image URL; fallback to other fields just in case
  const url =
    json?.data?.image?.url ||
    json?.data?.url ||
    json?.data?.display_url
  if (!url) throw new Error('imgbb response missing image URL')

  return url as string
}



  private async shareToTelegramStory(): Promise<void> {
    const shareableFortune = this.buildShareableFortune()
    if (!shareableFortune) {
      return
    }

    this.updateShareButtonLabel('Preparing…')
    this.shareBtn.disabled = true

    try {
      const shareUrl = await this.getReferralShareUrl()
      const shareText = `"${shareableFortune.message}"\n\nCrack your own fortune!`

      const telegramApp = (window as unknown as {
        Telegram?: { WebApp?: { shareToStory?: (url: string, params?: any) => void } }
      }).Telegram?.WebApp
      const shareToStory = telegramApp?.shareToStory

      if (typeof shareToStory !== 'function') {
        this.resetShareButtonState()
        this.shareBtn.disabled = false
        alert('Sharing to Stories is not supported in your Telegram app.')
        return
      }

      const imageBlob = await this.createFortuneShareImage(shareableFortune)
      const mediaUrl = await this.uploadToImgBB(imageBlob)
      console.log('Uploaded share image to imgbb:', mediaUrl)

      shareToStory(mediaUrl, {
        text: shareText,
        widget_link: { url: shareUrl, name: 'Play now' },
      })

      this.showShareFeedback('Shared to Story!')
    } catch (err) {
      const msg = this.errorToString(err)
      console.error('Story share failed:', err)
      alert('Unable to share to story right now.\n\n' + msg)
      this.resetShareButtonState()
      this.shareBtn.disabled = false
    }
  }

  private async shareToTelegramChat(): Promise<void> {
    const shareableFortune = this.buildShareableFortune()
    if (!shareableFortune) {
      return
    }

    const telegramApp = (window as unknown as {
      Telegram?: { WebApp?: { shareMessage?: (msgId: string, cb?: (sent?: boolean) => void) => void } }
    }).Telegram?.WebApp
    const shareMessage = telegramApp?.shareMessage
    if (typeof shareMessage !== 'function') {
      alert('Sharing to chats is not supported in your Telegram app.')
      return
    }

    const telegramUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id
    const userIdString = telegramUserId ? String(telegramUserId) : null
    if (!userIdString || !/^\d+$/.test(userIdString)) {
      alert('Sharing requires your Telegram account. Please relaunch the bot inside Telegram.')
      return
    }

    this.updateShareButtonLabel('Preparing…')
    this.shareBtn.disabled = true

    try {
      const shareUrl = await this.getReferralShareUrl()
      const imageBlob = await this.createFortuneShareImage(shareableFortune)
      const mediaUrl = await this.uploadToImgBB(imageBlob)

      const { prepared_message_id } = await apiService.createPreparedShareMessage(userIdString, {
        image_url: mediaUrl,
        image_width: this.shareImageWidth,
        image_height: this.shareImageHeight,
        fortune_text: shareableFortune.message,
        referral_url: shareUrl,
      })

      const shareSucceeded = await new Promise<boolean>((resolve, reject) => {
        let settled = false
        try {
          shareMessage(prepared_message_id, (sent?: boolean) => {
            settled = true
            resolve(sent !== false)
          })
        } catch (error) {
          reject(error)
          return
        }

        window.setTimeout(() => {
          if (!settled) {
            resolve(true)
          }
        }, 1500)
      })

      if (!shareSucceeded) {
        this.resetShareButtonState()
        this.shareBtn.disabled = false
        return
      }

      this.showShareFeedback('Shared to chat!')
    } catch (err) {
      const msg = this.errorToString(err)
      console.error('Chat share failed:', err)
      alert('Unable to share to chat right now.\n\n' + msg)
      this.resetShareButtonState()
      this.shareBtn.disabled = false
    }
  }

  private buildShareableFortune(): ShareableFortune | null {
    if (this.currentFortuneDetails) {
      const { message, tier, stars } = this.currentFortuneDetails
      return { message, tier, stars }
    }

    const domFortune = this.extractFortuneFromDom()
    if (domFortune) {
      return domFortune
    }

    const message = this.currentFortune.trim()
    if (!message) {
      return null
    }

    return { message }
  }

  private extractFortuneFromDom(): ShareableFortune | null {
    const messageEl = this.fortuneBox.querySelector('.fortune-message')
    const message = messageEl?.textContent?.trim()
    if (!message) {
      return null
    }

    const cardEl = this.fortuneBox.querySelector('.fortune-card')
    const tierAttr = cardEl?.getAttribute('data-tier')?.trim()
    const starsAttr = cardEl?.getAttribute('data-stars') ?? undefined

    let tierText = tierAttr || undefined
    if (!tierText) {
      const legacyTierEl = this.fortuneBox.querySelector('.fortune-tier')
      tierText = legacyTierEl?.textContent?.trim().toLowerCase()
    }

    let starCount: number | undefined
    if (starsAttr) {
      const parsedStars = parseInt(starsAttr, 10)
      if (!Number.isNaN(parsedStars)) {
        starCount = parsedStars
      }
    }

    if (typeof starCount === 'undefined') {
      const badgeEl = this.fortuneBox.querySelector('.rarity-badge')
      const badgeText = badgeEl?.textContent ?? ''
      const starMatches = badgeText.match(/★/g) ?? []
      if (starMatches.length > 0) {
        starCount = starMatches.length
      }
    }

    if (typeof starCount === 'undefined') {
      const legacyStarCount = this.fortuneBox.querySelectorAll('.fortune-stars i').length
      starCount = legacyStarCount > 0 ? legacyStarCount : undefined
    }

    return {
      message,
      tier: tierText || undefined,
      stars: starCount,
    }
  }

  private buildFortuneCard({
    message,
    tier,
    stars,
    quote = true,
  }: {
    message: string
    tier?: string | null
    stars?: number | null
    quote?: boolean
  }): string {
    const safeMessage = this.escapeHtml(message)
    const displayMessage = quote ? `&ldquo;${safeMessage}&rdquo;` : safeMessage

    const normalizedTier = tier ? tier.toLowerCase() : 'common'
    const tierLabel = tier ? tier.toUpperCase() : null
    const rarityClass = `rarity-${normalizedTier}`
    const rarityBorderClass = `rarity-border-${normalizedTier}`

    const safeStars = stars ? Math.max(0, Math.min(5, Math.floor(stars))) : 0
    const starsText = safeStars ? '★'.repeat(safeStars) : ''
    const badgeLabel = tierLabel
      ? `${tierLabel}${starsText ? ` ${starsText}` : ''}`
      : starsText

    const headerContent = badgeLabel
      ? `
        <div class="fortune-card-header">
          <span class="rarity-badge ${rarityClass}">
            ${badgeLabel}
          </span>
        </div>
      `
      : ''

    const dataAttrs = [
      tier ? `data-tier="${this.escapeHtml(normalizedTier)}"` : '',
      safeStars ? `data-stars="${safeStars}"` : '',
    ]
      .filter(Boolean)
      .join(' ')

    return `
      <div class="fortune-card fortune-card--inventory ${rarityBorderClass}" ${dataAttrs}>
        ${headerContent}
        <div class="fortune-card-body">
          <p class="fortune-message">${displayMessage}</p>
        </div>
      </div>
    `
  }


  


  private async getReferralShareUrl(): Promise<string> {
    // If referral URL is already available, return it
    if (this.referralUrl) {
      return this.referralUrl
    }

    // If referral URL is not available, trigger a state refresh and wait for it
    console.log('[UIManager] Referral URL not available, triggering state refresh...')
    
    return new Promise((resolve) => {
      // Set up a one-time listener for state updates
      const handleStateUpdate = () => {
        if (this.referralUrl) {
          window.removeEventListener('gameStateUpdated', handleStateUpdate)
          resolve(this.referralUrl)
        }
      }
      
      // Listen for state updates
      window.addEventListener('gameStateUpdated', handleStateUpdate)
      
      // Trigger state refresh
      window.dispatchEvent(new CustomEvent('refreshState'))
      
      // Fallback timeout - if state doesn't update within 5 seconds, use generic URL
      setTimeout(() => {
        window.removeEventListener('gameStateUpdated', handleStateUpdate)
        console.warn('[UIManager] Timeout waiting for referral URL, using generic URL')
        resolve('https://t.me/a_fortune_cookie_bot/cookie')
      }, 5000)
    })
  }

  private showShareFeedback(label: string): void {
    if (this.shareButtonResetTimer !== null) {
      window.clearTimeout(this.shareButtonResetTimer)
      this.shareButtonResetTimer = null
    }

    this.shareBtn.disabled = false
    this.shareBtn.innerHTML = `<i data-lucide="check"></i><span>${label}</span>`
    this.shareBtn.style.color = '#22c55e'
    createIcons({
      icons: { Check },
    })

    this.shareButtonResetTimer = window.setTimeout(() => {
      this.shareBtn.innerHTML = this.shareButtonDefaultHtml
      this.shareBtn.style.color = ''
      createIcons({
        icons: { Share2 },
      })
      this.shareButtonResetTimer = null
    }, 2200)
  }

  private updateShareButtonLabel(label: string): void {
    this.resetShareButtonState()
    const labelSpan = this.shareBtn.querySelector('span')
    if (labelSpan) {
      labelSpan.textContent = label
    }
  }

  private resetShareButtonState(): void {
    if (this.shareButtonResetTimer !== null) {
      window.clearTimeout(this.shareButtonResetTimer)
      this.shareButtonResetTimer = null
    }

    if (this.shareBtn.innerHTML !== this.shareButtonDefaultHtml) {
      this.shareBtn.innerHTML = this.shareButtonDefaultHtml
      this.shareBtn.style.color = ''
      createIcons({
        icons: { Share2 },
      })
    }
  }


  private async createFortuneShareImage(fortune: ShareableFortune): Promise<Blob> {
    await this.waitForShareFonts()

    const width = this.shareImageWidth
    const height = this.shareImageHeight
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Canvas context not supported')
    }

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height)
    gradient.addColorStop(0, '#fff8e6')
    gradient.addColorStop(0.6, '#ffe1bf')
    gradient.addColorStop(1, '#ffc8a0')
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)

    // Ambient shapes
    ctx.save()
    ctx.globalAlpha = 0.25
    ctx.fillStyle = '#ff6f91'
    ctx.beginPath()
    ctx.ellipse(width * 0.2, height * 0.22, 220, 220, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffb07c'
    ctx.beginPath()
    ctx.ellipse(width * 0.82, height * 0.18, 260, 260, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffd7b3'
    ctx.beginPath()
    ctx.ellipse(width * 0.82, height * 0.78, 300, 300, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()

    // Cookie illustration
    const cookieImage = await (this.fortuneImagePromise ??= this.loadImage('/xian-cookies/share-guy.png'))
    const cookieSize = 480
    ctx.save()
    ctx.shadowColor = 'rgba(255, 176, 124, 0.45)'
    ctx.shadowBlur = 60
    ctx.drawImage(cookieImage, width / 2 - cookieSize / 2, 140, cookieSize, cookieSize * (cookieImage.height / cookieImage.width))
    ctx.restore()


    // Fortune card
    const cardX = 120
    const cardY = 640
    const cardWidth = width - cardX * 2
    const cardHeight = 840

    ctx.save()
    ctx.shadowColor = 'rgba(255, 176, 124, 0.4)'
    ctx.shadowBlur = 50
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    this.drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, 48)
    ctx.fill()
    ctx.restore()

    ctx.save()
    ctx.textAlign = 'center'
    ctx.fillStyle = '#ff6f91'
    ctx.font = '600 64px "Fredoka", sans-serif'
    ctx.fillText('My Fortune Today', width / 2, cardY + 120)

    ctx.fillStyle = 'rgba(255, 176, 124, 0.35)'
    ctx.fillRect(cardX + 160, cardY + 160, cardWidth - 320, 6)

    const quote = `“${fortune.message}”`
    const maxTextWidth = cardWidth - 200
    let fontSize = 58
    let fortuneLines: string[] = []
    let lineHeight = fontSize * 1.2
    const textAreaTopBase = cardY + 260
    const textAreaBottom = cardY + cardHeight - 260
    const baseTextAreaHeight = textAreaBottom - textAreaTopBase
    const badgePaddingX = 120
    const badgePaddingY = 36
    let badgeWidth = 0
    let badgeHeight = 0
    let badgeText = ''
    let badgeStyle: { background: string; text: string; shadow: string; border: string } | null = null

    if (fortune.tier) {
      const rarityStyles: Record<string, { background: string; text: string; shadow: string; border: string }> = {
        common: { background: 'rgba(139, 69, 19, 0.9)', text: '#ffffff', shadow: 'rgba(139, 69, 19, 0.35)', border: 'rgba(255, 255, 255, 0.35)' },
        uncommon: { background: 'rgba(205, 133, 63, 0.92)', text: '#ffffff', shadow: 'rgba(205, 133, 63, 0.35)', border: 'rgba(255, 240, 220, 0.4)' },
        rare: { background: 'rgba(218, 165, 32, 0.92)', text: '#442b06', shadow: 'rgba(218, 165, 32, 0.35)', border: 'rgba(255, 244, 214, 0.7)' },
        epic: { background: 'rgba(153, 50, 204, 0.92)', text: '#ffffff', shadow: 'rgba(153, 50, 204, 0.35)', border: 'rgba(255, 230, 255, 0.5)' },
        legendary: { background: 'rgba(255, 69, 0, 0.92)', text: '#ffffff', shadow: 'rgba(255, 69, 0, 0.35)', border: 'rgba(255, 235, 220, 0.6)' },
        limited: { background: 'rgba(14, 165, 233, 0.92)', text: '#f8fbff', shadow: 'rgba(14, 165, 233, 0.35)', border: 'rgba(190, 230, 255, 0.7)' }
      }
      const tierKey = fortune.tier.toLowerCase()
      badgeStyle = rarityStyles[tierKey] ?? { background: 'rgba(255, 176, 124, 0.9)', text: '#4b2e05', shadow: 'rgba(255, 176, 124, 0.35)', border: 'rgba(255, 255, 255, 0.4)' }
      const displayTier = fortune.tier.charAt(0).toUpperCase() + fortune.tier.slice(1)
      const starCount = fortune.stars ? Math.max(0, Math.min(fortune.stars, 5)) : 0
      const starsText = starCount > 0 ? ` · ${'★'.repeat(starCount)}` : ''
      badgeText = `${displayTier}${starsText}`

      ctx.save()
      ctx.font = '600 44px "Fredoka", sans-serif'
      const metrics = ctx.measureText(badgeText)
      badgeWidth = metrics.width + badgePaddingX
      const textMetricsHeight = (metrics.actualBoundingBoxAscent ?? 0) + (metrics.actualBoundingBoxDescent ?? 0)
      badgeHeight = Math.max(textMetricsHeight, 44) + badgePaddingY
      ctx.restore()
    }

    const reservedForBadge = badgeHeight ? badgeHeight + 70 : 0
    const maxTextHeight = Math.max(baseTextAreaHeight - reservedForBadge, 0)

    do {
      ctx.font = `500 ${fontSize}px "Fredoka", sans-serif`
      lineHeight = fontSize * 1.2
      fortuneLines = this.wrapText(ctx, quote, maxTextWidth)
      if (fortuneLines.length * lineHeight <= maxTextHeight || fontSize <= 36) {
        break
      }
      fontSize -= 4
    } while (fontSize >= 36)

    ctx.fillStyle = '#4b2e05'
    ctx.font = `500 ${fontSize}px "Fredoka", sans-serif`
    lineHeight = fontSize * 1.2
    const textHeight = fortuneLines.length * lineHeight
    let textAreaTop = textAreaTopBase

    if (badgeStyle && badgeText) {
      ctx.save()
      ctx.font = '600 44px "Fredoka", sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const badgeX = width / 2 - badgeWidth / 2
      const badgeY = textAreaTop

      ctx.shadowColor = badgeStyle.shadow
      ctx.shadowBlur = 28
      this.drawRoundedRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, badgeHeight / 2)
      ctx.fillStyle = badgeStyle.background
      ctx.fill()
      ctx.shadowBlur = 0
      ctx.lineWidth = 2
      ctx.strokeStyle = badgeStyle.border
      ctx.stroke()

      ctx.fillStyle = badgeStyle.text
      ctx.fillText(badgeText, width / 2, badgeY + badgeHeight / 2)
      ctx.restore()

      textAreaTop = badgeY + badgeHeight + 70
    }

    const textAreaHeight = Math.max(0, textAreaBottom - textAreaTop)
    const textOffset = Math.max(0, (textAreaHeight - textHeight) / 2)
    let textY = textAreaTop + textOffset + lineHeight / 2
    fortuneLines.forEach((line) => {
      ctx.fillText(line, width / 2, textY)
      textY += lineHeight
    })

    
    ctx.restore()

    // Bottom ribbon
    ctx.save()
    const ribbonHeight = 160
    ctx.fillStyle = '#ff6f91'
    ctx.beginPath()
    ctx.moveTo(0, height - ribbonHeight)
    ctx.quadraticCurveTo(width / 2, height - ribbonHeight - 80, width, height - ribbonHeight)
    ctx.lineTo(width, height)
    ctx.lineTo(0, height)
    ctx.closePath()
    ctx.fill()

    ctx.font = '600 60px "Fredoka", sans-serif'
    ctx.fillStyle = '#ffffff'
    ctx.textAlign = 'center'
    ctx.fillText('Tap · Crack · Collect · Repeat', width / 2, height - 70)
    ctx.restore()

    const out = document.createElement('canvas')
    out.width = 720
    out.height = 1280
    const octx = out.getContext('2d')!
    octx.imageSmoothingEnabled = true
    octx.drawImage(canvas, 0, 0, out.width, out.height)
    return this.canvasToBlob(out)
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/)
    const lines: string[] = []
    let currentLine = ''

    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word
      if (ctx.measureText(testLine).width > maxWidth && currentLine) {
        lines.push(currentLine)
        currentLine = word
      } else {
        currentLine = testLine
      }
    })

    if (currentLine) {
      lines.push(currentLine)
    }

    return lines
  }

  private drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    const r = Math.min(radius, width / 2, height / 2)
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + width - r, y)
    ctx.quadraticCurveTo(x + width, y, x + width, y + r)
    ctx.lineTo(x + width, y + height - r)
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
    ctx.lineTo(x + r, y + height)
    ctx.quadraticCurveTo(x, y + height, x, y + height - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
  }

  private async loadImage(src: string): Promise<HTMLImageElement> {
  const url = new URL(src, window.location.origin);
  const href = url.href;

  // Try fetch -> blob -> object URL (better errors & fewer caching issues)
  try {
    const res = await fetch(href, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${href}`);
    const blob = await res.blob();
    const obj = URL.createObjectURL(blob);

    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(obj); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(obj); reject(e); };
      img.src = obj;
    });
  } catch (e) {
    // Fallback to direct load
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Image load failed for ${href}`));
      img.src = href + (href.includes('?') ? '&' : '?') + 'v=' + Date.now(); // quick cache-bust
    });
  }
}


  private async waitForShareFonts(): Promise<void> {
    if ('fonts' in document) {
      try {
        await (document.fonts as FontFaceSet).ready
      } catch {
        // Ignore font readiness errors
      }
    }
  }


  private canvasToBlob(canvas: HTMLCanvasElement, type = 'image/jpeg', quality = 0.9): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const toBlob = (cb: (b: Blob | null) => void) =>
        // some webviews expose toBlob but ignore type/quality; still try
        (canvas as any).toBlob ? canvas.toBlob(cb, type, quality) : cb(null)

      toBlob((blob: Blob | null) => {
        if (blob) return resolve(blob)
        // Fallback: use dataURL → Blob (works in WKWebView/Telegram)
        try {
          const dataUrl = canvas.toDataURL(type, quality)
          const b = this.dataURLToBlob(dataUrl)
          resolve(b)
        } catch (e) {
          reject(new Error('Failed to create share image'))
        }
      })
    } catch {
      try {
        const dataUrl = canvas.toDataURL(type, quality)
        const b = this.dataURLToBlob(dataUrl)
        resolve(b)
      } catch {
        reject(new Error('Failed to create share image'))
      }
    }
  })
}
private dataURLToBlob(dataURL: string): Blob {
  const [meta, b64] = dataURL.split(',')
  const mime = /data:(.*?);base64/.exec(meta)?.[1] || 'image/jpeg'
  const bin = atob(b64)
  const len = bin.length
  const arr = new Uint8Array(len)
  for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i)
  return new Blob([arr], { type: mime })
}
  renderRewards(state: GameState, tasks: TaskItem[]): void {
    // Update total cracked
    const totalCrackedEl = document.getElementById('totalCrackedRewards')
    if (totalCrackedEl) {
      totalCrackedEl.textContent = state.totalCracked.toLocaleString()
    }

    const exampleTokenCountEl = document.getElementById('exampleTokenCount')
    if (exampleTokenCountEl) {
      exampleTokenCountEl.textContent = state.totalCracked.toLocaleString()
    }

    // Define milestones based on counter tasks
    const milestones = [20, 50, 100, 250, 500, 1000]
    const nextMilestoneValue = milestones.find((milestone) => milestone > state.totalCracked)

    const milestonesEl = document.getElementById('milestonesList')
    if (milestonesEl) {
      const milestonesHtml = milestones
        .map((milestone) => {
          const completed = state.totalCracked >= milestone
          const isNext = !completed && nextMilestoneValue === milestone
          return `
            <div class="milestone-item ${completed ? 'completed' : ''} ${isNext ? 'next' : ''}">
              <div class="milestone-number">${milestone}</div>
              <div class="milestone-label">Total Cracks</div>
              <div class="milestone-boost">Bigger launch share</div>
            </div>
          `
        })
        .join('')
      milestonesEl.innerHTML = milestonesHtml
    }

    const milestoneCallout = document.getElementById('milestoneCallout')
    if (milestoneCallout) {
      if (nextMilestoneValue) {
        const remaining = nextMilestoneValue - state.totalCracked
        milestoneCallout.textContent = `You’re ${remaining} cracks away from ${nextMilestoneValue}.`
      } else {
        milestoneCallout.textContent = 'You’ve cleared every milestone—keep cracking for an even bigger slice.'
      }
    }

   

    // Count ready tasks
    const readyTasks = tasks.filter(task => task.ready && !task.claimed)
    const readyTasksSection = document.getElementById('readyTasksSection')
    const readyTasksCount = document.getElementById('readyTasksCount')
    
    if (readyTasksSection && readyTasksCount) {
      if (readyTasks.length > 0) {
        readyTasksCount.textContent = readyTasks.length.toString()
        readyTasksSection.style.display = 'block'
      } else {
        readyTasksSection.style.display = 'none'
      }
    }

    // Calculate and display estimated reward
    const estimatedRewardEl = document.getElementById('estimatedReward')
    if (estimatedRewardEl) {
      const rewardPerCrack = 0.02 // $0.02 example value per Total Crack
      const estimatedReward = state.totalCracked * rewardPerCrack
      estimatedRewardEl.textContent = `$${estimatedReward.toFixed(2)}`
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  destroy(): void {
    this.clearRefillTimer()
  }
}
